import { useEffect, useRef, useState } from 'react';
import { ActiveOrgProvider } from '../lib/activeOrg';
import { supabase } from '../lib/supabase';
import { AttachExistingModal } from '../components/projects/attach-existing/AttachExistingModal';
import type { AttachCandidate } from '../components/projects/attach-existing/types';

// Mock data lets Stream B build the UI before the real hooks exist, and drives
// the Phase-3 Playwright spec end-to-end against the REAL container (below).
export const MOCK_DOCS: AttachCandidate[] = [
  { id: 'd1', title: 'Onboarding spec', typeLabel: 'PRD', ownerName: 'Alice', lifecycle: 'published', updatedAt: '2026-05-30T10:00:00Z', createdAt: '2026-05-01T10:00:00Z', bodyExcerpt: 'How new orgs are onboarded…' },
  { id: 'd2', title: 'Billing design notes', typeLabel: 'Design', ownerName: 'Bob', lifecycle: 'draft', updatedAt: '2026-05-28T10:00:00Z', createdAt: '2026-04-15T10:00:00Z', bodyExcerpt: 'Stripe integration approach…' },
  { id: 'd3', title: 'Archived research', typeLabel: 'Research', ownerName: null, lifecycle: 'archived', updatedAt: '2026-03-01T10:00:00Z', createdAt: '2026-02-01T10:00:00Z', bodyExcerpt: 'Old findings.' },
];

const ORG_ID = 'org-harness';
const PROJECT_ID = 'p1';

// ---------------------------------------------------------------------------
// Backendless data injection for the harness.
//
// AttachExistingModal is backend-bound: its hooks (useAttachCandidates /
// useAttachFacets / useAttachMultiple) call the singleton `supabase` client
// directly — there is NO data-injection prop on the container, and the repo has
// no MSW. To drive the REAL container with MOCK_DOCS in a browser with no auth,
// the harness stubs only the exact supabase surface those hooks touch, scoped
// to this page (installed on mount, restored on unmount). This keeps the
// container's public API untouched.
//
// Surface the documents path touches:
//   rpc('list_org_members')            -> owner id→name map
//   rpc('list_documents')              -> the candidate rows
//   from('document_types').select().eq().is()  -> type id→name map
//   from('project_documents').select().eq()    -> already-attached ids (none)
// ---------------------------------------------------------------------------

// Stable synthetic owner/type ids so the hook's memberMap/typeMap resolve back
// to each MOCK_DOC's ownerName/typeLabel.
const OWNER_IDS: Record<string, string | null> = { d1: 'u-alice', d2: 'u-bob', d3: null };
const TYPE_IDS: Record<string, string | null> = { d1: 't-prd', d2: 't-design', d3: 't-research' };

const MEMBER_ROWS = [
  { user_id: 'u-alice', display_name: 'Alice' },
  { user_id: 'u-bob', display_name: 'Bob' },
];
const TYPE_ROWS = [
  { id: 't-prd', name: 'PRD' },
  { id: 't-design', name: 'Design' },
  { id: 't-research', name: 'Research' },
];
// list_documents rows reverse-mapped from MOCK_DOCS. body_preview→bodyExcerpt,
// last_event_at→updatedAt. total_count is (re)computed per-request in the stub's
// list_documents handler against the POST-p_q-filter set (the hook reads
// docRows[0].total_count for the paginated "X of Y" count), so it's omitted here.
const LIST_DOCUMENTS_ROWS = MOCK_DOCS.map((d) => ({
  id: d.id,
  type_id: TYPE_IDS[d.id] ?? null,
  title: d.title,
  body_preview: d.bodyExcerpt ?? null,
  owner_id: OWNER_IDS[d.id] ?? null,
  lifecycle: d.lifecycle,
  last_event_at: d.updatedAt,
  created_at: d.createdAt,
}));

// A minimal thenable that resolves to a Supabase-shaped { data, error } and is
// chainable through the filter builders the hooks call (.select/.eq/.is/.in).
function queryResult(data: unknown) {
  const result = { data, error: null as null };
  const builder: Record<string, unknown> = {
    select: () => builder,
    eq: () => builder,
    is: () => builder,
    in: () => builder,
    order: () => builder,
    // useAttachMultiple does supabase.from('project_documents').upsert(rows, opts)
    // and treats a no-error response as success. No-op resolving to the real
    // success shape so the modal's onDone()/onClose() path runs in the harness.
    upsert: () => Promise.resolve({ data: null, error: null }),
    then: (resolve: (v: typeof result) => unknown) => resolve(result),
  };
  return builder;
}

type SupabaseLike = {
  rpc: (fn: string, args?: unknown) => unknown;
  from: (table: string) => unknown;
};

function installSupabaseStub(): () => void {
  const client = supabase as unknown as SupabaseLike;
  const origRpc = client.rpc;
  const origFrom = client.from;

  client.rpc = (fn: string, args?: unknown) => {
    if (fn === 'list_org_members') return Promise.resolve({ data: MEMBER_ROWS, error: null });
    if (fn === 'list_documents') {
      // Mirror the real `list_documents` server-side title search: when p_q is a
      // non-empty string, narrow to rows whose title contains it (case-insensitive);
      // otherwise return the full set. Rebuild total_count on the first POST-FILTER
      // row so the hook's docRows[0].total_count reflects the filtered universe.
      const pQ = (args as { p_q?: string | null } | undefined)?.p_q;
      const q = typeof pQ === 'string' ? pQ.trim().toLowerCase() : '';
      const filtered = q
        ? LIST_DOCUMENTS_ROWS.filter((r) => (r.title ?? '').toLowerCase().includes(q))
        : LIST_DOCUMENTS_ROWS;
      const data = filtered.map((r, i) => ({
        ...r,
        total_count: i === 0 ? filtered.length : null,
      }));
      return Promise.resolve({ data, error: null });
    }
    // set_work_item_project (work-items attach) — succeed no-op.
    return Promise.resolve({ data: null, error: null });
  };
  client.from = (table: string) => {
    if (table === 'document_types') return queryResult(TYPE_ROWS);
    if (table === 'project_documents') return queryResult([]);
    // work_items candidate query (epics/work-items kinds) — empty universe.
    if (table === 'work_items') return queryResult([]);
    return queryResult([]);
  };

  return () => {
    client.rpc = origRpc;
    client.from = origFrom;
  };
}

/**
 * /__test/attach-existing-modal — Playwright-only harness mounting the REAL
 * AttachExistingModal (kind="documents") against MOCK_DOCS via a scoped
 * supabase stub + a synthetic ActiveOrgProvider. The Phase-3 spec can open the
 * modal panel (`attach-existing-modal-panel`), see the 3 mock rows ("Onboarding
 * spec", "Billing design notes", "Archived research"), select, preview, and
 * attach — all without a live backend or auth.
 */
export function TestAttachExistingModal() {
  const [open, setOpen] = useState(true);
  const [doneCount, setDoneCount] = useState(0);

  // Install the supabase stub before the modal's hooks fire. useRef + a layout
  // effect on first render keeps the stub active for the life of the page.
  const restoreRef = useRef<(() => void) | null>(null);
  if (restoreRef.current === null) {
    restoreRef.current = installSupabaseStub();
  }
  useEffect(() => {
    return () => {
      restoreRef.current?.();
      restoreRef.current = null;
    };
  }, []);

  return (
    <ActiveOrgProvider activeOrgId={ORG_ID} activeOrgName="Harness Org">
      <div data-testid="attach-existing-harness">
        <button type="button" data-testid="attach-existing-harness-open" onClick={() => setOpen(true)}>
          open
        </button>
        <pre data-testid="attach-existing-harness-state">
          {JSON.stringify({ open, doneCount, count: MOCK_DOCS.length })}
        </pre>
        <AttachExistingModal
          open={open}
          kind="documents"
          projectId={PROJECT_ID}
          onClose={() => setOpen(false)}
          onDone={() => setDoneCount((n) => n + 1)}
        />
      </div>
    </ActiveOrgProvider>
  );
}
